import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HardHat, Plus, ClipboardList, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtInt, fmtDate, today } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [pending, setPending] = useState([]);

  async function refresh() {
    const { data } = await api.get("/projects");
    setProjects(data);
    if (data.length && !selected) { setSelected(data[0]); }
  }
  useEffect(() => { refresh(); loadPending(); }, []);
  async function loadPending() {
    if (user?.role !== "administrator") return;
    try { const { data } = await api.get("/projects/approvals/pending"); setPending(data); } catch {}
  }
  async function review(capId, action, comments) {
    try {
      await api.post(`/projects/daily/${capId}/approval`, { action, comments: comments || "" });
      toast.success(`Capture ${action.replace("_", " ")}`);
      loadPending();
      if (selected) { const { data } = await api.get(`/projects/${selected.id}/daily`); setCaptures(data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  useEffect(() => {
    async function loadCaptures() {
      if (!selected) return;
      const { data } = await api.get(`/projects/${selected.id}/daily`);
      setCaptures(data);
    }
    loadCaptures();
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="projects-page">
      <PageHero
        eyebrow="Projects360"
        title="Construction management"
        subtitle="Construction tracking, daily progress and administrator approvals."
        moduleKind="projects"
      />

      {user?.role === "administrator" && pending.length > 0 && (
        <Card className="rounded-2xl border-gold/40 bg-gold/5" data-testid="approvals-card">
          <CardHeader>
            <CardTitle className="text-base">Pending approvals ({pending.length})</CardTitle>
            <CardDescription>Daily submissions awaiting administrator review.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Submitted by</TableHead><TableHead>Work</TableHead>
                <TableHead>Workers</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pending.map((c) => (
                  <TableRow key={c.id} data-testid={`pending-${c.id}`}>
                    <TableCell>{fmtDate(c.capture_date)}</TableCell>
                    <TableCell>{c.submitted_by_name}</TableCell>
                    <TableCell className="max-w-xs truncate">{c.work_done}</TableCell>
                    <TableCell>{fmtInt(c.workers_present)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => review(c.id, "approve")} data-testid={`approve-${c.id}`}>
                        <CheckCircle2 className="h-4 w-4 text-deepgreen" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => review(c.id, "request_revision", "Please revise")}>
                        <RotateCcw className="h-4 w-4 text-gold" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => review(c.id, "reject", "Rejected")}>
                        <XCircle className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <div className="section-title">Projects</div>
            <NewProject onCreated={refresh} />
          </div>
          {projects.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left rounded-2xl border p-4 transition-colors ${selected?.id === p.id ? "border-deepgreen bg-deepgreen/5" : "border-border hover:border-deepgreen/40"}`} data-testid={`project-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium">{p.name}</div>
                <Badge variant="outline">{p.phase}</Badge>
              </div>
              <div className="subtle mt-1">Target {fmtDate(p.target_completion)}</div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1"><span className="subtle">Completion</span><span className="font-medium">{p.completion_pct}%</span></div>
                <Progress value={p.completion_pct || 0} />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                <div><div className="subtle">Budget</div><div className="font-medium">{fmt(p.budget)}</div></div>
                <div><div className="subtle">Spend</div><div className="font-medium">{fmt(p.spend)}</div></div>
              </div>
            </button>
          ))}
          {projects.length === 0 && <div className="subtle">No projects yet.</div>}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {selected && (
            <>
              <Card className="rounded-2xl">
                <CardHeader className="flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle className="flex items-center gap-2"><HardHat className="h-4 w-4 text-deepgreen" />{selected.name}</CardTitle>
                    <CardDescription>{selected.site} · Supervisor: {selected.supervisor || "—"}</CardDescription>
                  </div>
                  <ProjectUpdate project={selected} onSaved={async () => { await refresh(); }} />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Phase" value={selected.phase} />
                    <Stat label="Completion" value={`${selected.completion_pct || 0}%`} />
                    <Stat label="Budget" value={fmt(selected.budget)} />
                    <Stat label="Spend" value={fmt(selected.spend)} />
                  </div>
                  {selected.notes && <div className="mt-4 text-sm text-foreground/80">{selected.notes}</div>}
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader className="flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-deepgreen" />Daily progress</CardTitle>
                    <CardDescription>{captures.length} entries</CardDescription>
                  </div>
                  <NewCapture projectId={selected.id} onSaved={async () => { const { data } = await api.get(`/projects/${selected.id}/daily`); setCaptures(data); }} />
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead>Work</TableHead><TableHead>Workers</TableHead>
                      <TableHead>Hours</TableHead><TableHead>Tomorrow</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {captures.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>{fmtDate(c.capture_date)}</TableCell>
                          <TableCell className="max-w-xs truncate">{c.work_done}</TableCell>
                          <TableCell>{fmtInt(c.workers_present)}</TableCell>
                          <TableCell>{fmt(c.hours_worked, 1)}</TableCell>
                          <TableCell className="max-w-xs truncate">{c.tomorrow_plan}</TableCell>
                        </TableRow>
                      ))}
                      {captures.length === 0 && <TableRow><TableCell colSpan={5} className="text-center subtle py-8">No captures yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-lg font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function NewProject({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phase: "Planning", start_date: today(), target_completion: "", completion_pct: 0, budget: 0, spend: 0, site: "Ngundu Homestead", supervisor: "", notes: "" });
  async function submit() {
    try { await api.post("/projects", { ...form, completion_pct: Number(form.completion_pct), budget: Number(form.budget), spend: Number(form.spend) }); toast.success("Project created"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" />New</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Phase</Label><Input value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} /></div>
          <div><Label>Site</Label><Input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} /></div>
          <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
          <div><Label>Target completion</Label><Input type="date" value={form.target_completion} onChange={(e) => setForm({ ...form, target_completion: e.target.value })} /></div>
          <div><Label>Budget</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
          <div><Label>Completion %</Label><Input type="number" value={form.completion_pct} onChange={(e) => setForm({ ...form, completion_pct: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectUpdate({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ phase: project.phase, completion_pct: project.completion_pct || 0, spend: project.spend || 0, notes: project.notes || "" });
  useEffect(() => { setForm({ phase: project.phase, completion_pct: project.completion_pct || 0, spend: project.spend || 0, notes: project.notes || "" }); }, [project]);
  async function submit() {
    try { await api.patch(`/projects/${project.id}`, { ...form, completion_pct: Number(form.completion_pct), spend: Number(form.spend) }); toast.success("Project updated"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="update-project-btn">Update</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Update project</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Phase</Label><Input value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} /></div>
          <div><Label>Completion %</Label><Input type="number" value={form.completion_pct} onChange={(e) => setForm({ ...form, completion_pct: e.target.value })} /></div>
          <div><Label>Spend</Label><Input type="number" value={form.spend} onChange={(e) => setForm({ ...form, spend: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCapture({ projectId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ capture_date: today(), work_done: "", workers_present: 0, hours_worked: 0, materials_used: "", equipment_used: "", comments: "", tomorrow_plan: "" });
  async function submit() {
    try { await api.post(`/projects/${projectId}/daily`, { ...form, project_id: projectId, workers_present: Number(form.workers_present), hours_worked: Number(form.hours_worked) }); toast.success("Capture saved"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-capture-btn"><Plus className="h-4 w-4 mr-1" />Log day</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Daily construction capture</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Date</Label><Input type="date" value={form.capture_date} onChange={(e) => setForm({ ...form, capture_date: e.target.value })} /></div>
          <div><Label>Workers present</Label><Input type="number" value={form.workers_present} onChange={(e) => setForm({ ...form, workers_present: e.target.value })} /></div>
          <div><Label>Hours worked</Label><Input type="number" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} /></div>
          <div><Label>Equipment used</Label><Input value={form.equipment_used} onChange={(e) => setForm({ ...form, equipment_used: e.target.value })} /></div>
          <div className="col-span-2"><Label>Materials used</Label><Textarea value={form.materials_used} onChange={(e) => setForm({ ...form, materials_used: e.target.value })} /></div>
          <div className="col-span-2"><Label>Work done today</Label><Textarea value={form.work_done} onChange={(e) => setForm({ ...form, work_done: e.target.value })} /></div>
          <div className="col-span-2"><Label>Tomorrow's plan</Label><Textarea value={form.tomorrow_plan} onChange={(e) => setForm({ ...form, tomorrow_plan: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
